/**
 * Cache-warmer for the three tenant-scoped ETag-cached endpoints. Lives in
 * its own module so the etag interceptor (and its spec) stays free of the
 * apiClient → MSAL import chain (msal-instance has top-level await, which
 * Jest can't load).
 *
 * Closes a startup race: `configHook.tsx`'s `[tenant_id]` effect dispatches
 * `getAllActiveTenantConfigs` / `getAllTenantFieldOptions` /
 * `getAllTenantDefects` *before* `<OfflineServiceWorker />` has mirrored
 * `enable_offline_checks` into the module-level flag. The etag interceptor
 * short-circuits when `isEnabled()` is false, so those very first responses
 * never land in the etag-cache. On the next 6-minute poll the cache catches
 * up — but only on non-config pages (the polling interval is gated on
 * `!param_tenant_id` — pre-existing behaviour from June 2024, see
 * `configHook.tsx` line 94), and an operator who loses connectivity in the
 * first six minutes after login would otherwise have no cached
 * `configs/active` ETag, which the offline session uses as `config_hash`.
 *
 * The same race exists for `/layers/offline-cache/` and is solved
 * symmetrically by `layerCache.refresh()` invoked from
 * `<OfflineServiceWorker />`.
 *
 * Tradeoff: this fires three redundant GETs once per login for offline
 * tenants. We considered un-gating the interceptor (always cache,
 * regardless of the master flag) but rejected it because the design
 * contract is "interceptor gated entirely behind enable_offline_checks"
 * — non-offline tenants should not silently start serving stale cached
 * bodies on transient network errors. See design §5.5.1.
 *
 * Failures are swallowed via `Promise.allSettled` — warming is best-effort
 * and must never break login.
 */

import { callAvosApi } from "../utils/useAvosApiHook";
import { getOfflineFeatureEnabled } from "./featureFlag";

export async function warmETagCache(tenant_id: string): Promise<void> {
    if (!tenant_id || !getOfflineFeatureEnabled()) return;
    const urls = [
        `/tenants/${tenant_id}/configs/active`,
        `/tenants/${tenant_id}/form-field-options/all`,
        `/tenants/${tenant_id}/defects/all`,
    ];
    await Promise.allSettled(urls.map((url) => callAvosApi(url, { no_toast: true })));
}
